import { useEffect, useMemo, useRef, useState } from 'react';
import { ClipPrompt } from '../../api/structs/clip';
import { MediaItem } from '../../api/structs/media';
import API from '../../api/api';
import { applyClipEditorPatch, ClipEditorPatch, MediaMetadataDrafts } from '../../api/clipEditor';
import { useToast } from '../../hooks/useToast';
import { useClipEditorContext } from '../../context/ClipEditorContext';

interface UseClipDraftEditorResult {
  draftClipMetadata: Record<string, any>;
  draftImages: MediaItem[];
  draftAiVideos: MediaItem[];
  draftAudios: MediaItem[];
  pendingSaves: number;
  isRenderStale: boolean;
  openEditor: (clipId: string) => void;
  handlePendingSaveChange: (delta: number) => void;
  waitForPendingSaves: () => Promise<void>;
  markDraftDirty: () => void;
}

const applyMediaMetadataDrafts = <T extends { id: string; metadata?: Record<string, any> }>(
  items: T[],
  draftMediaMetadata: MediaMetadataDrafts
): T[] =>
  items.map((item) => {
    const draft = draftMediaMetadata[item.id];
    return draft
      ? {
          ...item,
          metadata: {
            ...(item.metadata || {}),
            ...draft,
          },
        }
      : item;
  });

/**
 * Owns the "AI Editor" draft-patch lifecycle for a single clip: pending clip/media
 * metadata edits staged from ClipEditorContext (chat-driven patches), the
 * in-flight save counter used to gate Render, and syncing/reverting/accepting
 * those drafts against the backend.
 */
export function useClipDraftEditor(
  clip: ClipPrompt,
  images: MediaItem[],
  aiVideos: MediaItem[],
  audios: MediaItem[],
  onRefresh: () => void
): UseClipDraftEditorResult {
  const toast = useToast();
  const [pendingSaves, setPendingSaves] = useState(0);
  const [localDraftDirty, setLocalDraftDirty] = useState(false);
  const [draftClipMetadata, setDraftClipMetadata] = useState<Record<string, any>>(() => clip.metadata || {});
  const [draftMediaMetadata, setDraftMediaMetadata] = useState<MediaMetadataDrafts>({});
  const pendingSaveCountRef = useRef(0);
  const pendingSaveWaitersRef = useRef<Array<() => void>>([]);
  const {
    openEditor,
    activeClipId,
    draftPatch,
    acceptPatchTrigger,
    revertPatchTrigger,
    setDraftPatch,
  } = useClipEditorContext();

  useEffect(() => {
    setDraftClipMetadata(clip.metadata || {});
    setDraftMediaMetadata({});
    if (!clip.is_dirty) {
      setLocalDraftDirty(false);
    }
  }, [clip.id, clip.metadata, clip.is_dirty]);

  const handlePendingSaveChange = (delta: number) => {
    pendingSaveCountRef.current = Math.max(0, pendingSaveCountRef.current + delta);
    setPendingSaves(pendingSaveCountRef.current);
    if (pendingSaveCountRef.current === 0) {
      const waiters = pendingSaveWaitersRef.current;
      pendingSaveWaitersRef.current = [];
      waiters.forEach((resolve) => resolve());
    }
  };

  const waitForPendingSaves = async () => {
    if (pendingSaveCountRef.current === 0) return;
    await new Promise<void>((resolve) => {
      pendingSaveWaitersRef.current.push(resolve);
    });
  };

  // Memoized so a re-render with unchanged inputs hands out the SAME arrays.
  // These identities are load-bearing downstream: MediaEditorSection rebuilds
  // its scene groups on [images, aiVideos] change, and the scene regenerate
  // modal re-reads run-owned frames on scene identity change — fresh arrays
  // minted per render turned any steady re-render source into scene-group
  // rebuilds and a getMediaItem request flood (2026-09-01).
  const draftImages = useMemo(
    () => applyMediaMetadataDrafts(images, draftMediaMetadata),
    [images, draftMediaMetadata]
  );
  const draftAiVideos = useMemo(
    () => applyMediaMetadataDrafts(aiVideos, draftMediaMetadata),
    [aiVideos, draftMediaMetadata]
  );
  const draftAudios = useMemo(
    () => applyMediaMetadataDrafts(audios, draftMediaMetadata),
    [audios, draftMediaMetadata]
  );

  const handleClipEditorDraftPatch = (patch: ClipEditorPatch) => {
    const applied = applyClipEditorPatch(draftClipMetadata, draftMediaMetadata, patch);
    setDraftClipMetadata(applied.metadata);
    setDraftMediaMetadata(applied.mediaMetadataDrafts);
    setLocalDraftDirty(true);
  };

  const handleClipEditorRevert = () => {
    setDraftClipMetadata(clip.metadata || {});
    setDraftMediaMetadata({});
    if (!clip.is_dirty) {
      setLocalDraftDirty(false);
    }
  };

  const handleClipEditorAccept = async (patch: ClipEditorPatch) => {
    handlePendingSaveChange(1);
    try {
      if (patch.clip_metadata && Object.keys(patch.clip_metadata).length > 0) {
        const savedMetadata = applyClipEditorPatch(clip.metadata || {}, {}, patch).metadata;
        await API.editClipPrompt(clip.id, { metadata: savedMetadata });
      }

      const mediaItems = [...images, ...aiVideos, ...audios];
      await Promise.all(
        (patch.media_metadata || []).map((item) => {
          const source = mediaItems.find((mediaItem) => mediaItem.id === item.media_id);
          if (!source) return Promise.resolve();
          return API.replaceMediaMetadata(item.media_id, {
            ...(source.metadata || {}),
            ...(item.metadata || {}),
          });
        })
      );

      setDraftMediaMetadata({});
      onRefresh();
    } finally {
      handlePendingSaveChange(-1);
    }
  };

  useEffect(() => {
    if (activeClipId === clip.id && draftPatch) {
      handleClipEditorDraftPatch(draftPatch);
    }
  }, [draftPatch, activeClipId, clip.id]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (activeClipId === clip.id && draftPatch) {
      handleClipEditorAccept(draftPatch)
        .then(() => {
          setDraftPatch(null);
          toast({ text: 'AI edits saved', level: 'success' });
        })
        .catch((error: any) => {
          toast({ text: `Failed to save AI edits: ${error.message}`, level: 'error' });
        });
    }
  }, [acceptPatchTrigger]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (activeClipId === clip.id) {
      handleClipEditorRevert();
    }
  }, [revertPatchTrigger]); // eslint-disable-line react-hooks/exhaustive-deps

  return {
    draftClipMetadata,
    draftImages,
    draftAiVideos,
    draftAudios,
    pendingSaves,
    isRenderStale: Boolean(clip.is_dirty || localDraftDirty),
    openEditor,
    handlePendingSaveChange,
    waitForPendingSaves,
    markDraftDirty: () => setLocalDraftDirty(true),
  };
}
